import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Components } from "@thisgorilla/primate-ds";
import { $getRoot } from "lexical";
import { useRef, useState } from "react";
import styled from "styled-components";

import { API_CALLS } from "../../../../API/api_calls";
import { useAppSelector } from "../../../../redux";
import {
  isFormSubmitButtonDisabled,
  validFile,
  validImageFileTypes,
} from "../../../../utils";
import { EmojiPicker } from "../../EmojiPicker";
import { FlexRow, FlexRowSpaced } from "../../Styles";
import { PostCommentFieldProps } from "../PostCommentField";
import GIF from "./GIF";
import ImgPreview from "./ImgPreview";
import UrlPreview from "./UrlPreview";
import { CommentsProps } from "../../../../redux/slices/comments/comments";

const Footer = ({
  formik,
  urlData,
  setTemp_id,
  temp_id,
  post_id,
  parent_comment_id,
  setIsFooterShow,
  comment_id,
  isEditMode,
  onClose,
  scrollAreaId,
  is_anonymous
}: PostCommentFieldProps.Footer) => {
  const { values, isValid } = formik;
  const [editor] = useLexicalComposerContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPreviewShow, setIsPreviewShow] = useState(true);
  const [showEmojiOptions, setShowEmojiOptions] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { allPostComments } = useAppSelector((state) => state.comments);
  const { page_size, page, comments } = allPostComments[post_id];
  const comment = comments.find((c) => c.id === comment_id);
  const [isGIFUploaded, setIsGIFUploaded] = useState(false);

  const intialGIF =
    isEditMode && comment && comment.gifs.length > 0
      ? {
          gif_id: comment.gifs[0].gif_id,
          id: comment.gifs[0].id,
          name: comment.gifs[0].name,
          type: comment.gifs[0].type,
          url: comment.gifs[0].url,
        }
      : null;
  const [commentGIF, setCommentGIF] = useState<CommentsProps.GIF | null>(
    intialGIF
  );

  const closeComment = (e: any) => {
    e.stopPropagation();
    onClose();
    setIsFooterShow(false);
    setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);

    if (temp_id) {
      !isEditMode &&
        API_CALLS.COMMENTS.deleteATemporaryCommentImage(
          {
            temp_id,
            post_id,
          },
          setTemp_id,
          setUploadedFile,
          setIsImageUploading
        );
    }
    if (commentGIF && !isEditMode) {
      API_CALLS.COMMENTS.deleteATemporaryCommentGif(
        {
          id: commentGIF.id,
          post_id,
        },
        setCommentGIF,
        setIsImageUploading
      );
    }
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      setUploadedFile(null);
      setTemp_id(null);
    });
  };

  const submitComment = (e: any) => {
    e.stopPropagation();
    if (isValid) {
      (isEditMode
        ? API_CALLS.COMMENTS.updateAComment({
            content: values.content,
            post_id,
            parent_comment_id: parent_comment_id,
            image: temp_id ? { id: temp_id } : null,
            page,
            page_size,
            comment_id: comment_id as string,
            gif: commentGIF,
            is_anonymous
          })
        : API_CALLS.COMMENTS.createANewComment({
            content: values.content,
            post_id,
            parent_comment_id: parent_comment_id,
            image: temp_id ? { id: temp_id } : null,
            page,
            page_size,
            gif: commentGIF,
            is_anonymous
          })
      ).then(() => {
        editor.update(() => {
          setIsFooterShow(false);
          onClose();
          setUploadedFile(null);
          setTemp_id(null);
          const root = $getRoot();
          root.clear();
          setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }, 0);
        });
      });
    }
  };

  return (
    <>
      {commentGIF && (
        <GIF
          commentGIF={commentGIF}
          setCommentGIF={setCommentGIF}
          post_id={post_id}
          setIsImageUploading={setIsImageUploading}
          isGIFUploaded={isGIFUploaded}
          isEditMode={isEditMode}
        />
      )}
      {urlData && isPreviewShow && (
        <UrlPreview urlData={urlData} setIsPreviewShow={setIsPreviewShow} />
      )}
      {!isImageUploading && (temp_id || uploadedFile) && (
        <ImgPreview
          uploadedFile={uploadedFile}
          temp_id={temp_id}
          setTemp_id={setTemp_id}
          setUploadedFile={setUploadedFile}
          post_id={post_id}
          setIsImageUploading={setIsImageUploading}
          isEditMode={isEditMode}
          comment_id={comment_id}
        />
      )}
      <FlexRowSpaced>
        <IconButtonContainer $gapInPx={8}>
          <Components.IconButton
            disabled={
              (uploadedFile ? true : false) || (commentGIF ? true : false)
            }
            iconType="Image"
            kind="Secondary"
            onClick={() => fileInputRef?.current?.click()}
            size="Small"
          />
          <EmojiPicker
            scrollAreaId={scrollAreaId}
            setShowEmojiOptions={setShowEmojiOptions}
            showEmojiOptions={showEmojiOptions}
            onGifSelected={(gif) => {
              API_CALLS.COMMENTS.uploadATemporaryCommentGif(
                {
                  gif_id: gif.id,
                  name: gif.title,
                  type: gif.type,
                  url: gif.images.original.url,
                  post_id,
                },
                setCommentGIF,
                setIsImageUploading,
                setIsGIFUploaded
              );
              setShowEmojiOptions(false);
            }}
          >
            <Components.IconButton
              disabled={
                (uploadedFile ? true : false) || (commentGIF ? true : false)
              }
              iconType="Sticker"
              kind="Secondary"
              onClick={() => {
                setShowEmojiOptions(true);
              }}
              size="Small"
            />
          </EmojiPicker>
        </IconButtonContainer>
        <FlexRow $gapInPx={8}>
          <Components.Tooltip type="label" label="Cancel">
            <Components.IconButton
              iconType="X"
              kind="Secondary"
              onClick={closeComment}
              size="Small"
              disabled={isImageUploading}
            />
          </Components.Tooltip>
          <Components.Tooltip type="label" label="Comment">
            <Components.IconButton
              iconType="Send"
              kind="Primary"
              onClick={submitComment}
              size="Small"
              disabled={
                (isFormSubmitButtonDisabled({
                  formik,
                  requiredFileds: { content: true },
                }) &&
                  ((temp_id ? false : true) || (commentGIF ? false : true))) ||
                isImageUploading
              }
            />
          </Components.Tooltip>
        </FlexRow>
      </FlexRowSpaced>
      {!uploadedFile && (
        <HiddenInput
          type="file"
          multiple={false}
          disabled={false}
          onChange={(e) => {
            const files = e.target.files;
            if (files?.length) {
              const validFiles = validFile(files);
              if (validFiles.length > 0) {
                setUploadedFile(validFiles[0]);
                API_CALLS.COMMENTS.uploadATemporaryCommentImage(
                  {
                    image: validFiles[0],
                    post_id,
                  },
                  setTemp_id,
                  setIsImageUploading
                );
              }
            }
          }}
          ref={fileInputRef}
          id="image-upload"
          accept={validImageFileTypes.join(",")}
        />
      )}
    </>
  );
};

export default Footer;

const IconButtonContainer = styled(FlexRow)`
  position: relative;
`;

const HiddenInput = styled.input`
  display: none;
`;
